import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageMeta from "../../components/PageMeta";
import ProductCard from "../../components/ProductCard";
import FilterSidebar from "../../components/FilterSidebar";
import FloatingCart from "../../components/FloatingCart";
import CartDrawer from "../../components/CartDrawer";
import api from "../../utils/api";
import { getCartItems, addToCart } from "../../utils/cartUtils";
import { getStockStatusFromProduct } from "../../utils/productStockUtils";
import { toLocalizedNumber } from "../../utils/localeUtils";
import {
  FaSearch,
  FaTimes,
  FaSlidersH,
  FaLeaf,
  FaStar,
  FaMapMarkerAlt,
  FaTags,
} from "react-icons/fa";
import "./Marketplace.css";
import "./ContentPage.css";

const Marketplace = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [organicOnly, setOrganicOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState("newest");
  const [activeFilters, setActiveFilters] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        if (parsed?.role === "farmer") {
          navigate("/farmer/dashboard", { replace: true });
          return;
        }
      } catch (e) {
        /* ignore */
      }
    }
    setCartItems(getCartItems());
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    products,
    searchQuery,
    selectedCategory,
    priceRange,
    selectedProvince,
    selectedDistrict,
    organicOnly,
    minRating,
    sortBy,
  ]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/products");
      const data = response.data || [];
      const transformed = data
        .filter((p) => p.farmer?.verified === true)
        .map((p) => {
          const addr = p.farmer?.address || {};
          return {
            _id: p._id,
            name: p.name,
            price: p.price,
            unit: p.unit,
            category: (p.category || "").toLowerCase(),
            // Keep raw image sources; resolve with getImageUrl at render-time
            image: p.images?.length > 0 ? p.images[0] : null,
            images: Array.isArray(p.images) ? p.images : [],
            farmer: {
              _id: p.farmer?._id || p.farmer,
              name: p.farmer?.name || "Farmer",
              email: p.farmer?.email || "",
              phone: p.farmer?.phone || "",
              address: p.farmer?.address || {},
              province: addr.province || p.farmer?.province || "",
              district: addr.district || p.farmer?.district || "",
              profileImage: p.farmer?.profileImage || null,
              verified: p.farmer?.verified || false,
              username: p.farmer?.username || "",
              rating: p.farmer?.rating || 0,
            },
            stockQuantity: p.quantity ?? 0,
            stock: getStockStatusFromProduct({ quantity: p.quantity }),
            isOrganic: p.isOrganic || false,
            organic: p.isOrganic || false,
            rating: p.rating || 0,
            numReviews: p.numReviews || 0,
            location: p.location || {},
            createdAt: p.createdAt,
          };
        });
      setProducts(transformed);
    } catch (e) {
      console.error("Error fetching products:", e);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.farmer?.name || "").toLowerCase().includes(q) ||
          (p.farmer?.district || "").toLowerCase().includes(q),
      );
    }
    if (selectedCategory !== "all")
      filtered = filtered.filter((p) => p.category === selectedCategory);
    filtered = filtered.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1],
    );
    if (selectedProvince)
      filtered = filtered.filter((p) => p.farmer.province === selectedProvince);
    if (selectedDistrict)
      filtered = filtered.filter((p) => p.farmer.district === selectedDistrict);
    if (organicOnly)
      filtered = filtered.filter((p) => p.isOrganic || p.organic);
    if (minRating > 0)
      filtered = filtered.filter((p) => (p.farmer?.rating || 0) >= minRating);
    switch (sortBy) {
      case "price-low":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "rating":
        filtered.sort((a, b) => b.farmer.rating - a.farmer.rating);
        break;
      default:
        break;
    }
    const filters = [];
    if (selectedCategory !== "all")
      filters.push({
        type: "category",
        value: selectedCategory,
        icon: <FaTags />,
      });
    if (priceRange[0] > 0 || priceRange[1] < 1000)
      filters.push({
        type: "price",
        value: `Rs.${priceRange[0]}–${priceRange[1]}`,
        icon: null,
      });
    if (selectedProvince)
      filters.push({
        type: "province",
        value: selectedProvince,
        icon: <FaMapMarkerAlt />,
      });
    if (selectedDistrict)
      filters.push({
        type: "district",
        value: selectedDistrict,
        icon: <FaMapMarkerAlt />,
      });
    if (organicOnly)
      filters.push({
        type: "organic",
        value: t("filter.organicOnly"),
        icon: <FaLeaf />,
      });
    if (minRating > 0)
      filters.push({
        type: "rating",
        value: `${minRating}+`,
        icon: <FaStar />,
      });
    setActiveFilters(filters);
    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const removeFilter = (type) => {
    switch (type) {
      case "category":
        setSelectedCategory("all");
        break;
      case "price":
        setPriceRange([0, 1000]);
        break;
      case "province":
        setSelectedProvince("");
        break;
      case "district":
        setSelectedDistrict("");
        break;
      case "organic":
        setOrganicOnly(false);
        break;
      case "rating":
        setMinRating(0);
        break;
      default:
        break;
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setPriceRange([0, 1000]);
    setSelectedProvince("");
    setSelectedDistrict("");
    setOrganicOnly(false);
    setMinRating(0);
    setSortBy("newest");
  };

  const indexOfLast = currentPage * productsPerPage;
  const currentProducts = filteredProducts.slice(0, indexOfLast);
  const hasMore = indexOfLast < filteredProducts.length;

  const handleAddToCart = (product) => {
    if (!user || user.role !== "consumer") {
      navigate("/login");
      return;
    }
    const updated = addToCart(product, 1);
    setCartItems(updated);
  };

  return (
    <div className="mp-page">
      <PageMeta
        title={t("marketplace.pageTitle")}
        description={t("marketplace.pageDescription")}
        path="/market"
      />
      <Header />

      <p className="marketplace-intro">
        {t("marketplace.intro")}{" "}
        <Link to="/guides">{t("marketplace.guidesLink")}</Link>
      </p>

      {/* Search Bar */}
      <div className="mp-search-bar">
        <div className="mp-search-inner">
          <div className="mp-search-field">
            <FaSearch className="mp-search-icon" />
            <input
              type="text"
              className="mp-search-input"
              placeholder={t("marketplace.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="mp-search-clear"
                onClick={() => setSearchQuery("")}
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Active Filter Pills */}
          {activeFilters.length > 0 && (
            <div className="mp-pills">
              {activeFilters.map((f, i) => (
                <span key={i} className="mp-pill">
                  {f.icon && <span className="mp-pill-icon">{f.icon}</span>}
                  {f.value}
                  <button onClick={() => removeFilter(f.type)}>
                    <FaTimes />
                  </button>
                </span>
              ))}
              <button className="mp-pill-reset" onClick={resetFilters}>
                {t("common.resetAll")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="mp-body">
        <div className="mp-layout">
          {/* Sidebar */}
          <aside
            className={`mp-sidebar ${mobileFilterOpen ? "mp-sidebar--open" : ""}`}
          >
            <div className="mp-sidebar-head">
              <h3>{t("filter.title")}</h3>
              <button
                className="mp-sidebar-close"
                onClick={() => setMobileFilterOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <FilterSidebar
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              selectedProvince={selectedProvince}
              setSelectedProvince={setSelectedProvince}
              selectedDistrict={selectedDistrict}
              setSelectedDistrict={setSelectedDistrict}
              organicOnly={organicOnly}
              setOrganicOnly={setOrganicOnly}
              minRating={minRating}
              setMinRating={setMinRating}
              resetFilters={resetFilters}
            />
          </aside>
          {mobileFilterOpen && (
            <div
              className="mp-overlay"
              onClick={() => setMobileFilterOpen(false)}
            />
          )}

          {/* Content */}
          <main className="mp-content">
            {/* Toolbar */}
            <div className="mp-toolbar">
              <div className="mp-toolbar-left">
                <button
                  className="mp-filter-toggle"
                  onClick={() => setMobileFilterOpen(true)}
                >
                  <FaSlidersH /> {t("filter.title")}
                </button>
                <span className="mp-result-count">
                  {loading ? (
                    "..."
                  ) : (
                    <>
                      {toLocalizedNumber(filteredProducts.length)}{" "}
                      {t("marketplace.productsFound")}
                    </>
                  )}
                </span>
              </div>
              <div className="mp-toolbar-right">
                <select
                  className="mp-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="newest">{t("marketplace.sortNewest")}</option>
                  <option value="price-low">
                    {t("marketplace.sortPriceLow")}
                  </option>
                  <option value="price-high">
                    {t("marketplace.sortPriceHigh")}
                  </option>
                  <option value="rating">{t("marketplace.sortRating")}</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="mp-loading">
                <div className="mp-loading-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <p>{t("marketplace.loadingProducts")}</p>
              </div>
            ) : currentProducts.length === 0 ? (
              <div className="mp-empty">
                <div className="mp-empty-icon-wrap">
                  <FaSearch />
                </div>
                <h3>{t("marketplace.noMatch")}</h3>
                <p>{t("marketplace.tryAdjusting")}</p>
                <button
                  className="mp-btn mp-btn--primary"
                  onClick={resetFilters}
                >
                  {t("common.resetFilters")}
                </button>
              </div>
            ) : (
              <>
                <div className="mp-grid">
                  {currentProducts.map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      user={user}
                      onAddToCart={handleAddToCart}
                    />
                  ))}
                </div>

                {hasMore && (
                  <div className="mp-load-more-wrap">
                    <button
                      className="mp-btn mp-btn--outline"
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                    >
                      {t("marketplace.loadMore")}
                    </button>
                    <span className="mp-load-more-info">
                      {t("marketplace.showing", {
                        start: toLocalizedNumber(1),
                        end: toLocalizedNumber(currentProducts.length),
                        total: toLocalizedNumber(filteredProducts.length),
                      })}
                    </span>
                  </div>
                )}

                {!hasMore && filteredProducts.length > productsPerPage && (
                  <div className="mp-load-more-wrap">
                    <span className="mp-load-more-info">
                      {t("marketplace.showing", {
                        start: toLocalizedNumber(1),
                        end: toLocalizedNumber(filteredProducts.length),
                        total: toLocalizedNumber(filteredProducts.length),
                      })}
                    </span>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {user && user.role === "consumer" && (
        <FloatingCart
          itemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
          onClick={() => setCartOpen(true)}
        />
      )}
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItems}
        setCartItems={setCartItems}
      />
      <Footer />
    </div>
  );
};

export default Marketplace;
